import { Transaction, TxnCategories } from "types/Models/books";
import { emptyApi } from "./emptyApi";
import {
  transactionFilterValuesToParams,
  TransactionQuery,
} from "./transactions";
import { parse } from "qs";
import {
  categoryFilterValuesToParams,
  GeneralLedgerParams,
} from "./generalLedger";
import {
  journalEntryFilterValuesToParams,
  JournalEntryParams,
} from "./journalEntry";
import {
  interactiveReportFilterValuesToParams,
  InteractiveNewReportParams,
} from "./reports";
import { JournalEntry } from "types/Models/journalEntry";
import { addGroupNotes } from "apis/serviceTeamGroups";
import qs from "qs";

export const COMMENT_TYPE = {
  TRANSACTION_COMMENT: "TRANSACTION_COMMENT",
  LEDGER_ENTRY_COMMENT: "LEDGER_ENTRY_COMMENT",
  TRANSACTION_CATEGORY_COMMENT: "TRANSACTION_CATEGORY_COMMENT",
  GENERAL_LEDGER_COMMENT: "GENERAL_LEDGER_COMMENT",
  FINANCIAL_CLOSING_COMMENT: "FINANCIAL_CLOSING_COMMENT",
  BALANCE_SHEET_COMMENT: "BALANCE_SHEET_COMMENT",
  CASH_FLOW_COMMENT: "CASH_FLOW_COMMENT",
  INCOME_STATEMENT_COMMENT: "INCOME_STATEMENT_COMMENT",
  VENDOR_SUMMARY_COMMENT: "VENDOR_SUMMARY_COMMENT",
  CUSTOM_REPORT_COMMENT: "CUSTOM_REPORT_COMMENT",
  TICKET_COMMENT: "TICKET_COMMENT",
} as const;

export type CommentType = (typeof COMMENT_TYPE)[keyof typeof COMMENT_TYPE];

export type Note = {
  uuid: string;
  description: string;
  created_at: string;
  updated_at: string;
  created_by?: {
    name: string;
    uuid: string;
    profile_url: string;
  };
  parent_object_id: string;
  is_pinned: boolean;
  comment_type: CommentType;
  tagged_user_profiles: {
    name: string;
    uuid: string;
    profile_url: string;
  }[];
  is_edited: boolean | null;
  has_actionable?: boolean;
};

export type NoteResponse = {
  comments: Note[];
  ledger_entry?: Pick<JournalEntry, "uuid" | "entry_name" | "date">;
  transaction?: Pick<Transaction, "uuid" | "amount" | "date" | "merchant"> & {
    category_id?: string;
  };
  category?: Pick<
    TxnCategories,
    "uuid" | "opening_balance" | "identifier" | "name"
  > & {
    group_id?: string;
  };
  total_pages: number;
  current_page: number;
  per_page: number;
  total_count: number;
};

export type AllNote = {
  uuid: string;
  source: string;
  source_id: string;
  source_name: string;
  note: string;
  created_by: {
    name: string;
    uuid: string;
    profile_url: string;
  };
  created_at: string;
  is_pinned: boolean;
  is_edited: boolean | null;
  has_actionable?: boolean;
  is_internal_ticket?: boolean;
  assignee?: { assignee_name: string; assignee_uuid: string }[];
};

export type UnifyNotesApiResponse = {
  all_notes: AllNote[];
  total_notes: number;
  total_pages: number;
  current_page: number;
  per_page: number;
  total_count: number;
};

export type groupNotesResponse = {
  notes: Note[];
  total_pages: number;
  current_page: number;
  per_page: number;
  total_count: number;
};

const notesApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAllUnifyNotes: build.query<
      UnifyNotesApiResponse,
      { groupId: string; page_num: number; isArchived?: boolean | null }
    >({
      query: ({ groupId, page_num, isArchived }) => {
        let queryUrl = qs.stringify(
          {
            is_archived: isArchived,
            page_num,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/notes/company/group/${groupId}/all_notes/${queryUrl}`,
        };
      },
      providesTags: (result) => (result ? ["NOTES", "PIN_NOTES"] : []),
    }),
    groupNotes: build.query<
      groupNotesResponse,
      {
        groupId: string;
        page_num: number;
        isArchived?: boolean | null;
      }
    >({
      query: ({ groupId, page_num, isArchived }) => {
        let queryUrl = qs.stringify(
          {
            is_archived: isArchived,
            page_num,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/notes/company/group/${groupId}/${queryUrl}`,
        };
      },
      providesTags: (result) => (result ? ["NOTES", "PIN_NOTES"] : []),
    }),

    pinNote: build.mutation<
      any,
      {
        noteId: string;
        is_pinned?: boolean;
        description?: string;
        is_archived?: boolean;
        tagged_profile_ids?: string[];
      }
    >({
      query: ({ noteId, ...arg }) => ({
        url: `/api/inkle/notes/${noteId}`,
        method: "PUT",
        body: arg,
      }),
      invalidatesTags: ["PIN_NOTES"],
    }),

    addGroupNotesMeeting: build.mutation<
      any,
      {
        groupId: string;
        payload: {
          description: string;
          last_contact_date?: string;
          next_contact_date?: string;
        };
      }
    >({
      query: ({ groupId, payload }) => ({
        url: `/api/inkle/notes/company/group/${groupId}/meeting/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["NOTES"],
    }),

    addGroupNotes: build.mutation<
      any,
      {
        groupId: string;
        payload: {
          description: string;
        };
      }
    >({
      query: ({ groupId, payload }) => ({
        url: `/api/inkle/notes/company/group/${groupId}/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["NOTES"],
    }),

    getNotes: build.query<
      NoteResponse[],
      {
        entityId: string;
        types: (Note["comment_type"] | "REPORTS_COMMENT")[];
        transactionFilters?: TransactionQuery;
        generalLedgerFilters?: GeneralLedgerParams;
        journalEntryFilters?: JournalEntryParams;
        reportFilters?: InteractiveNewReportParams;
        search?: string;
      }
    >({
      query: ({
        entityId,
        types,
        transactionFilters,
        generalLedgerFilters,
        journalEntryFilters,
        reportFilters,
        search,
      }) => ({
        url: `/api/inkle/notes/entity/${entityId}/comments/`,
        params: {
          comment_types: types.join(","),
          comment_search_term: search,
          ...parse(
            transactionFilters
              ? transactionFilterValuesToParams(transactionFilters)
              : "",
            {
              ignoreQueryPrefix: true,
            }
          ),
          ...parse(
            generalLedgerFilters
              ? categoryFilterValuesToParams(generalLedgerFilters)
              : "",
            {
              ignoreQueryPrefix: true,
            }
          ),
          ...parse(
            journalEntryFilters
              ? journalEntryFilterValuesToParams(journalEntryFilters)
              : "",
            {
              ignoreQueryPrefix: true,
            }
          ),
          ...parse(
            reportFilters
              ? interactiveReportFilterValuesToParams(reportFilters)
              : "",
            {
              ignoreQueryPrefix: true,
            }
          ),
        },
      }),
      providesTags: (result) =>
        result ? ["NOTES", "BOOKS_TRANSACTIONS", "JOURNAL_ENTRIES"] : [],
    }),

    getNote: build.query<
      NoteResponse,
      {
        parentObjId: string;
        entityId: string;
        is_private?: boolean;
        page_num: number;
        isArchived?: boolean;
        commentType?: CommentType;
      }
    >({
      query: ({
        entityId,
        parentObjId,
        is_private,
        page_num,
        isArchived,
        commentType,
      }) => {
        let queryUrl = qs.stringify(
          {
            is_private: is_private,
            is_archived: isArchived,
            page_num,
            comment_type: commentType,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return `/api/inkle/notes/entity/${entityId}/parent_object/${parentObjId}/comment/${queryUrl}`;
      },
      providesTags: ["NOTES", "PIN_NOTES"],
    }),

    createNotes: build.mutation<
      NoteResponse[],
      {
        parentObjId: string;
        entityId: string;
        payload: {
          description?: string;
          mentioned_stream_user_ids?: string[];
          tagged_profile_ids?: string[];
          comment_type: Note["comment_type"];
          report_id?: string;
          group_id?: string;
          is_private?: boolean;
          has_actionable?: boolean;
        };
      }
    >({
      query: ({ entityId, parentObjId, payload }) => ({
        url: `/api/inkle/notes/entity/${entityId}/parent_object/${parentObjId}/comment/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              "NOTES",
              "BOOKS_SINGLE_TRANSACTION",
              "ChatTickets",
              "FINANCIAL_CLOSING_COMMENTS",
              "CATEGORISATION_HISTORY_TXNS",
            ]
          : [],
    }),
  }),
});

export const {
  useCreateNotesMutation,
  useGetNotesQuery,
  useGetNoteQuery,
  useLazyGetNoteQuery,
  useGroupNotesQuery,
  useLazyGroupNotesQuery,
  useAddGroupNotesMeetingMutation,
  useAddGroupNotesMutation,
  usePinNoteMutation,
  useGetAllUnifyNotesQuery,
  useLazyGetAllUnifyNotesQuery,
} = notesApis;
